import {
  PaperPlaneTilt,
  UserCirclePlus,
  UserMinus,
  UsersThree,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useState } from 'react';
import { ProtectedFragment } from '@/state/store/auth/protected-fragment';
import { IamData } from '../../types/user.types';
import {
  DUMMY_ROLE_OPTIONS,
  formatLastLogin,
  getFullName,
  getPrimaryRoleName,
  getUserAccountStatus,
  type UserAccountStatus,
} from '../../utils/users-roles.utils';
import { UserStatusBadge } from '../user-status-badge/user-status-badge';

type UsersRolesTableProps = {
  users: IamData[];
  isLoading?: boolean;
  isError?: boolean;
  hasActiveFilters: boolean;
  onRowClick: (user: IamData) => void;
  onResendActivation: (user: IamData) => void;
};

const RowIconButton = ({
  icon: Icon,
  label,
  danger,
  disabled,
  onClick,
}: {
  icon: typeof PaperPlaneTilt;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className={danger ? 'size-8 text-error hover:bg-error-background hover:text-error' : 'size-8'}
    aria-label={label}
    title={disabled ? `${label} — not available yet` : label}
    disabled={disabled}
    onClick={onClick}
  >
    <Icon size={16} />
  </Button>
);

/**
 * Role picker — options are the design-reference role set, not a real role
 * catalog (none exists yet), so a change here is local-only and not persisted.
 */
const RoleSelectCell = ({ user, status }: { user: IamData; status: UserAccountStatus }) => {
  const initialRole = getPrimaryRoleName(user);
  const matchedInitial = DUMMY_ROLE_OPTIONS.find(
    (option) => option.toLowerCase() === initialRole?.toLowerCase()
  );
  const [selectedRole, setSelectedRole] = useState(matchedInitial);

  return (
    <Select
      value={selectedRole}
      onValueChange={setSelectedRole}
      disabled={status === 'deactivated'}
    >
      <SelectTrigger
        className="h-[34px] w-full bg-white"
        aria-label={`Role for ${getFullName(user)}`}
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {DUMMY_ROLE_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const UsersRolesTable = ({
  users,
  isLoading,
  isError,
  hasActiveFilters,
  onRowClick,
  onResendActivation,
}: UsersRolesTableProps) => {
  return (
    <Table className="min-w-[760px]" divClassname="min-w-0 overflow-x-auto">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="sticky top-0 z-10 bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
            Name
          </TableHead>
          <TableHead className="sticky top-0 z-10 w-44 bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
            Role
          </TableHead>
          <TableHead className="sticky top-0 z-10 bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
            Status
          </TableHead>
          <TableHead className="sticky top-0 z-10 bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
            Last login
          </TableHead>
          <TableHead className="sticky top-0 z-10 bg-muted/40 px-4 text-right text-xs font-semibold text-muted-foreground">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && (
          <TableRow>
            <TableCell colSpan={5} className="py-14 text-center text-sm text-muted-foreground">
              Loading members…
            </TableCell>
          </TableRow>
        )}

        {!isLoading && isError && (
          <TableRow>
            <TableCell colSpan={5} className="py-14">
              <div className="mx-auto flex max-w-sm flex-col items-center gap-3 text-center">
                <WarningCircle className="size-8 text-error" aria-hidden />
                <p className="font-semibold text-foreground">Couldn&apos;t load members</p>
                <p className="text-sm text-muted-foreground">
                  Something went wrong while fetching your organisation&apos;s members.
                </p>
              </div>
            </TableCell>
          </TableRow>
        )}

        {!isLoading && !isError && users.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="py-14">
              <div className="mx-auto flex max-w-sm flex-col items-center gap-3 text-center">
                <UsersThree className="size-8 text-muted-foreground" aria-hidden />
                <p className="font-semibold text-foreground">
                  {hasActiveFilters ? 'No members match' : 'No members yet'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'Try a different search term, or clear your status and role filters.'
                    : 'Invite your first teammate to start collaborating on this organisation.'}
                </p>
              </div>
            </TableCell>
          </TableRow>
        )}

        {!isLoading &&
          !isError &&
          users.map((user) => {
            const status = getUserAccountStatus(user);

            return (
              <TableRow
                key={user.itemId}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => onRowClick(user)}
              >
                <TableCell className="px-4 py-3.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {getFullName(user)}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3.5" onClick={(event) => event.stopPropagation()}>
                  <RoleSelectCell user={user} status={status} />
                </TableCell>
                <TableCell className="px-4 py-3.5">
                  <UserStatusBadge status={status} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3.5 text-sm text-muted-foreground">
                  {formatLastLogin(user.lastLoggedInTime)}
                </TableCell>
                <TableCell className="px-4 py-3.5" onClick={(event) => event.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {status === 'invited' && (
                      <ProtectedFragment roles={['admin']}>
                        <RowIconButton
                          icon={PaperPlaneTilt}
                          label="Resend invite"
                          onClick={() => onResendActivation(user)}
                        />
                        <RowIconButton icon={XCircle} label="Revoke invite" danger disabled />
                      </ProtectedFragment>
                    )}
                    {status === 'active' && (
                      <ProtectedFragment roles={['admin']}>
                        <RowIconButton icon={UserMinus} label="Deactivate user" danger disabled />
                      </ProtectedFragment>
                    )}
                    {status === 'deactivated' && (
                      <ProtectedFragment roles={['admin']}>
                        <RowIconButton icon={UserCirclePlus} label="Reactivate user" disabled />
                      </ProtectedFragment>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};
